import { useState, useEffect, useMemo, ReactNode } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RoleContext } from '@/contexts/role-context-impl';
import { useFreelancerProfileId } from '@/lib/hooks/use-freelancer-profile-id';
import { useClientProfileId } from '@/lib/hooks/use-client-profile-id';
import { userService } from '@/lib/api/services/user.service';
import type { UserRole } from '@/types/role';

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  const [activeRole, setActiveRoleState] = useState<UserRole | null>(null);
  const [hasReloaded, setHasReloaded] = useState(false);

  // Force reload user data on mount to get fresh publicMetadata
  // This fixes the issue where JWT has stale metadata after role changes
  useEffect(() => {
    if (isLoaded && user && !hasReloaded) {
      user.reload().then(() => {
        setHasReloaded(true);
      }).catch(() => {
        // Silently fail - user will still work with potentially stale data
        setHasReloaded(true);
      });
    }
  }, [isLoaded, user, hasReloaded]);

  // Define default routes for each role
  const getDefaultRoute = (role: UserRole): string => {
    switch (role) {
      case 'admin':
        return '/admin?tab=freelancers';
      case 'client':
        return '/client/dashboard';
      case 'freelancer':
        return '/genius/dashboard';
      default:
        return '/';
    }
  };

  // Fetch user data to determine available roles
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Use backend API to get user data
      const data = await userService.getMe();
      return {
        is_genius: data.is_genius,
        is_client: data.is_client,
      };
    },
    enabled: !!user?.id,
  });

  // Check for admin claim in JWT
  const isAdmin = user?.publicMetadata?.app_role === 'admin';

  // Get freelancer profile ID when user has freelancer capabilities
  const { profileId: freelancerProfileId, isLoading: isLoadingFreelancerId } = useFreelancerProfileId(userData?.is_genius ? user?.id : undefined);

  // Get client profile ID when user has client capabilities
  const { profileId: clientProfileId, isLoading: isLoadingClientId } = useClientProfileId(userData?.is_client ? user?.id : undefined);

  // Determine available roles
  // Admins get admin first so it's their default, plus client access for viewing
  const availableRoles = useMemo<UserRole[]>(() => {
    const roles: UserRole[] = [];

    // Admin role comes first so it's the default
    if (isAdmin) {
      roles.push('admin');
    }

    // Admins also get client access for viewing the platform
    if (userData?.is_client || isAdmin) {
      roles.push('client');
    }

    if (userData?.is_genius) {
      roles.push('freelancer');
    }

    return roles;
  }, [userData?.is_client, userData?.is_genius, isAdmin]);

  // Initialize role on first load only
  // This effect only runs when activeRole is null (initial state)
  // Wait for hasReloaded to ensure we have fresh publicMetadata (fixes admin role not showing on first login)
  useEffect(() => {
    if (!isLoaded || !user) return;
    if (!hasReloaded) return; // Wait for user.reload() to complete with fresh metadata
    if (availableRoles.length === 0) return;
    // Only set role on initial load - don't override user's explicit switches
    if (activeRole !== null) return;

    const savedRole = localStorage.getItem(`activeRole_${user.id}`) as UserRole | null;

    // If saved role is valid, use it
    if (savedRole && availableRoles.includes(savedRole)) {
      setActiveRoleState(savedRole);
    } else if (isAdmin && availableRoles.includes('admin')) {
      // Default admins to admin role if no valid saved role
      setActiveRoleState('admin');
      localStorage.setItem(`activeRole_${user.id}`, 'admin');
    } else if (availableRoles.length > 0) {
      // Default to first available role
      setActiveRoleState(availableRoles[0]);
    }
  }, [user, isLoaded, availableRoles, isAdmin, activeRole, hasReloaded]);

  // Save role to localStorage when it changes and navigate to default route
  const setActiveRole = (role: UserRole) => {
    if (!user?.id) return;

    if (availableRoles.includes(role)) {
      const previousRole = activeRole;
      setActiveRoleState(role);
      localStorage.setItem(`activeRole_${user.id}`, role);

      // Navigate to default route if role actually changed
      if (previousRole && previousRole !== role) {
        const defaultRoute = getDefaultRoute(role);
        navigate(defaultRoute);
      }
    }
  };

  // Include profile ID loading states to prevent race conditions
  // Only wait for profile ID if user has that role
  const isLoadingProfileIds =
    (userData?.is_genius === true && isLoadingFreelancerId) ||
    (userData?.is_client === true && isLoadingClientId);

  // Include hasReloaded to prevent UI flash before fresh metadata is available
  const isLoading = !isLoaded || !hasReloaded || isLoadingUser || isLoadingProfileIds;

  return (
    <RoleContext.Provider value={{
      activeRole,
      setActiveRole,
      availableRoles,
      isLoading,
      freelancerProfileId: freelancerProfileId ?? null,
      clientProfileId: clientProfileId ?? null
    }}>
      {children}
    </RoleContext.Provider>
  );
}
